<template>
    <v-container class="pa-5" fluid>
        <page-title>Raportowanie / Raportowanie papieru</page-title>
        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12" md="6" lg="8">
                            <h2 class="text-h6">
                                Witaj {{ userName }}
                            </h2>

                            <p class="mt-2">
                                Dziś jest {{ currentDate }}
                            </p>
                            <p class="mt-2">
                                Aktualna zmiana: {{ userShift }}
                            </p>
                        </v-col>
                        <v-col cols="12" md="6" lg="4">
                            <WorkspaceSelector
                                :show-workspace-settings-dialog="showWorkspaceSettingsDialog"
                                :can-change-workspace="canChangeWorkspace"
                                :current-workspace="currentWorkspace"
                                @change-workspace="saveWorkspaceSettings"
                            />
                        </v-col>
                    </v-row>
                </v-container>

                <v-divider class="my-4" />

                <template>
                    <v-row justify="end" no-gutters>
                        <v-col cols="auto">
                            <v-btn
                                color="green"
                                text
                                outlined
                                :disabled="activeReportItemsLoading"
                                @click="updateTables"
                            >
                                <v-icon
                                    v-if="!activeReportItemsLoading"
                                    color="green"
                                >
                                    mdi-refresh
                                </v-icon>
                                <v-progress-circular
                                    v-else
                                    indeterminate
                                    color="green"
                                    class="mr-1"
                                    size="16"
                                    width="2"
                                />
                                Odśwież
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <ReportingProcessingReportsTable
                                    :headers="activeReportItemsHeaders"
                                    :items="activeReportItems"
                                    :is-loading="activeReportItemsLoading"
                                    @open-report="openReport"
                                />
                            </v-card>
                        </v-col>
                        <v-col cols="12">
                            <ReportingPaperReportsTable
                                :headers="reportItemHeaders"
                                :items="reportItems"
                                :is-loading="reportItemsLoading"
                            />
                        </v-col>
                    </v-row>
                </template>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import { errorHandler } from '@/utils/errorHandler'
import dateFormatter from '@/mixins/dateFormatter'
import PageTitle from '@/components/common/PageTitle'
import ReportingProcessingReportsTable from '../Common/ReportingProcessingReportsTable'
import WorkspaceSelector from '../Common/WorkspaceSelector'
import ReportingPaperReportsTable from '../Common/ReportingPaperReportsTable'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'PaperReporting',
    components: {
        PageTitle,
        WorkspaceSelector,
        ReportingProcessingReportsTable,
        ReportingPaperReportsTable,
    },
    mixins: [ dateFormatter ],
    data() {
        return {
            currentWorkspace: null,
            showWorkspaceSettingsDialog: false,

            activeReportItems: [],
            activeReportItemsLoading: false,
            activeReportItemsHeaders: [
                {
                    text: 'Numer',
                    value: 'objectNumber',
                    sortable: false
                },
                {
                    text: 'Maszyna',
                    value: 'machineSymbol',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Proces',
                    value: 'processName',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Montaż',
                    value: 'plateNumber',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Nakład wykonany',
                    value: 'reportAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Ilość osób',
                    value: 'machineStuffAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Status',
                    value: 'statusId',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data rozpoczęcia',
                    value: 'start',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],

            reportItems: [],
            reportItemsLoading: false,
            reportItemHeaders: [
                {
                    text: 'Nr zlecenia',
                    value: 'procNumber',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Montaż',
                    value: 'procPlateNumber',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Sposób druku',
                    value: 'printMethod',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Rodzaj surowca',
                    value: 'paper',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Format surowca',
                    value: 'format',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Nakład brutto',
                    value: 'procTaskQuantity',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Zaplanowany start',
                    value: 'procTaskStartTime',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Czas druku',
                    value: 'procTaskDuration',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Kolorystyka str. A',
                    value: 'coloursPageA',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Kolorystyka str. B',
                    value: 'coloursPageB',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Lakier str. A',
                    value: 'varnishSideA',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Lakier str. B',
                    value: 'varnishSideB',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Liczarka (co ile arkuszy)',
                    value: 'inserterBatch',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Status materiałów',
                    value: 'materialStatus',
                    align: 'center',
                    sortable: false,
                },
            ],
        }
    },
    computed: {
        userName() {
            return this.$store.state.user.userName
        },
        userShift() {
            return this.currentWorkspace ? this.currentWorkspace.userShift : 'brak danych'
        },
        currentMachine() {
            return this.currentWorkspace?.machine
        },
        currentDate() {
            return this.formatDate(new Date(), true, true, { weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric' })
        },
        canChangeWorkspace() {
            return hasUserPermission('production_sites_admin_update')
        },
    },
    mounted() {
        const workspace = JSON.parse(localStorage.getItem('workspace.paper.reporting'))

        if ( workspace && workspace.workspaceId) {
            this.saveWorkspaceSettings(workspace.workspaceId)
        } else {
            this.showWorkspaceSettingsDialog = true
        }
    },
    methods: {
        getReportItems() {
            this.reportItemsLoading = true

            this.$store.dispatch('reports/getScheduledTasks', { machineCode: this.currentMachine?.code })
                .then(response => {
                    this.reportItems = response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.reportItemsLoading = false
                })
        },
        getActiveReports() {
            this.activeReportItemsLoading = true
            this.$store.dispatch('reports/getActiveReports', {
                query: `?machineId=${this.currentMachine?.id}`,
            })
                .then(response => {
                    this.activeReportItems = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.activeReportItemsLoading = false
                })
        },
        updateTables() {
            this.getActiveReports()
            this.getReportItems()
        },
        async setWorkspaceData(workspaceId = null) {
            return await this.$store.dispatch('configurations/getProductionSite', workspaceId)
                .then(response => {
                    this.currentWorkspace = response.data
                    localStorage.setItem('workspace.paper.reporting', JSON.stringify({
                        'workspaceId': response.data.id,
                    }))
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async saveWorkspaceSettings(workspaceId) {
            await this.setWorkspaceData(workspaceId)

            this.getActiveReports()
            this.getReportItems()
        },
        openReport(reportId) {
            router.push({
                name: 'reportsDetailedPaperReports',
                params: {
                    id: reportId,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
    },
}
</script>
